import { Form, FormItemProps, Input, Select, SelectProps } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import { DefaultOptionType } from 'antd/es/select';
import MultiSelect from 'components/commons/MultiSelect';
import FormItem from 'components/form/FormItem';
import InfinitySelect, {
  InfinitySelectProps,
} from 'components/form/InfinitySelect';
import { useRestContext } from 'components/form/RestInputContext';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface IFormSelect
  extends Pick<FormItemProps, 'name' | 'required' | 'rules'> {
  reference?: InfinitySelectProps;
  options?: DefaultOptionType[];
  label?: React.ReactNode;
  type?: 'single' | 'array';
  placeholder?: string;
  showInSideInput?: boolean;
  extraOnChage?: (e: string, r?: any) => void;
  selectProps?: SelectProps;
  initialValue?: string | number | boolean;
  nameFormItem?: NamePath;
  noStyle?: boolean;
  isCustomSelect?: boolean;
  className?: string;
}

export default function FormSelect({
  reference,
  options,
  name,
  nameFormItem,
  label,
  type = 'single',
  placeholder,
  showInSideInput = false,
  extraOnChage,
  required,
  rules = [],
  selectProps,
  initialValue,
  noStyle,
  isCustomSelect,
  className,
}: IFormSelect) {
  const { form, record } = useRestContext();
  const [initialSelected, setInitialSelected] = useState<string[]>([]);
  const { t } = useTranslation();
  const onChange = (e, r) => {
    // console.log(e, 'e');
    extraOnChage && extraOnChage(e, r);
    if (Array.isArray(e)) {
      setInitialSelected(e);
      form.setFieldValue(
        name,
        form.getFieldValue(name)
          ? [...form.getFieldValue(name), ...e.map((item) => ({ id: item }))]
          : [{ id: e[0] }],
      );
    } else {
      setInitialSelected([e]);
      form.setFieldValue(name, e);
    }
    isCustomSelect && form.validateFields([name]);
  };

  const formOptions = useMemo(() => {
    return {
      getValueFromEvent: (e) => {
        extraOnChage?.(e);
        return e;
      },
    };
  }, []);

  useEffect(() => {
    if (get(record, name) && !nameFormItem) {
      setInitialSelected(
        get(record, reference?.formKey ? [reference?.formKey, ...name] : name),
      );
    } else {
      setInitialSelected(
        form.getFieldValue(
          reference?.formKey ? [reference?.formKey, ...name] : name,
        ),
      );
    }
  }, [record, reference]);

  const onClose = (id, value) => {
    form.setFieldValue(
      name,
      value.filter((v) => id !== v?.id),
    );
    isCustomSelect && form.validateFields([name]);
  };

  if (isCustomSelect && reference) {
    return (
      <FormItem
        name={nameFormItem || name}
        label={label}
        rules={[
          {
            required,
            message: t('common.error.requiredCommon'),
          },
        ]}
        className={className}
      >
        <div>
          <Form.Item
            noStyle
            shouldUpdate={(p, c) => get(p, name) !== get(c, name)}
          >
            {({ getFieldValue }) => (
              <InfinitySelect
                {...reference}
                placeholder={placeholder}
                onChange={onChange}
                selected={getFieldValue(name)}
                name={name}
                onClose={(id) => onClose(id, getFieldValue(name))}
              />
            )}
          </Form.Item>
        </div>
      </FormItem>
    );
  }

  if (type === 'array' && showInSideInput) {
    const value =
      form.getFieldValue(
        reference?.formKey ? [reference?.formKey, ...name] : name,
      ) || [];

    const onClose = (e) => {
      form.setFieldsValue({
        [name]: value.filter((v) => e !== v?.id),
      });
    };

    return (
      <FormItem
        name={nameFormItem || name}
        label={label}
        noStyle={noStyle}
        rules={required ? [{ required: true }] : rules}
        className={className}
      >
        <InfinitySelect
          type="array"
          selected={initialSelected}
          onChange={onChange}
          onClose={onClose}
          placeholder={placeholder}
          showInSideInput={showInSideInput}
          name={name}
          {...reference}
          selectProps={selectProps}
        />
      </FormItem>
    );
  }

  if (type === 'array' && !showInSideInput) {
    return (
      <>
        {!nameFormItem && (
          <div className="d-none">
            <Form.List name={name}>
              {(fields) =>
                fields.map(({ name: _name }) => (
                  <Form.Item
                    noStyle={noStyle}
                    name={
                      reference?.childFormKey
                        ? [_name, reference?.childFormKey]
                        : [_name]
                    }
                  >
                    <Input placeholder={placeholder} />
                  </Form.Item>
                ))
              }
            </Form.List>
          </div>
        )}
        <Form.Item
          label={label}
          noStyle={noStyle}
          shouldUpdate={(o, n) => get(o, name) !== get(n, name)}
        >
          {({ getFieldValue, setFieldValue }) => {
            const value = getFieldValue(name) || [];
            const onClose = (e) => {
              setFieldValue(
                name,
                value.filter((v) => e !== v?.id),
              );
            };
            const handleChange = (e) => {
              setFieldValue(name, [
                ...value,
                ...e.map((item) => ({ value: item })),
              ]);
            };
            return reference ? (
              <InfinitySelect
                type="array"
                selected={value}
                onChange={onChange}
                onClose={onClose}
                placeholder={placeholder}
                showInSideInput={showInSideInput}
                name={name}
                {...reference}
              />
            ) : (
              <MultiSelect
                handleChange={handleChange}
                selected={value}
                onClose={onClose}
                options={options}
                placeholder={placeholder}
              />
            );
          }}
        </Form.Item>
      </>
    );
  }

  if (reference) {
    return (
      <FormItem
        name={nameFormItem || name}
        label={label}
        noStyle={noStyle}
        rules={required ? [{ required: true }] : rules}
        className={className}
      >
        <InfinitySelect
          {...reference}
          placeholder={placeholder}
          onChange={onChange}
          selected={initialSelected}
          name={name}
        />
      </FormItem>
    );
  }

  return (
    <FormItem
      name={nameFormItem || name}
      label={label}
      rules={required ? [{ required: true }] : rules}
      initialValue={initialValue}
      formOptions={formOptions}
      noStyle={noStyle}
    >
      <Select options={options} placeholder={placeholder} {...selectProps} />
    </FormItem>
  );
}
